import React, { useEffect, useRef, useState } from 'react';
import Loader from '../Loader';
import Alert from '../Alert';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import DeleteAccountModal from './DeleteAccountModal';
import feather from 'feather-icons';
import { getUserTimezone } from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';
import { FiInfo, FiCamera, FiTrash2, FiEye, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import '../../styles/AccountToggleOptions.css';
import { SERVER_BASE_URL } from '../../config/app.config';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useSheetTouchIsolation from '../../hooks/useSheetTouchIsolation';
import AttachmentFilePreview from '../Attachments/AttachmentFilePreview';
import {
    ATTACHMENT_ACCEPT,
    isPreviewableImage,
    validateAttachmentFile,
} from '../../util/attachmentHelper';

const AddEditAccountBottomSheet = ({
    show,
    onClose,
    onAccountAction,
    mode = 'add',
    accountData = null,
    setShowToast,
    setShowToastForDelete,
}) => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState(accountData?.type || 'cash');
    const [showInHome, setShowInHome] = useState(accountData?.show_in_home ?? true);
    const [liquidMoney, setLiquidMoney] = useState(accountData?.is_liquid ?? true);
    const [hoverInfo, setHoverInfo] = useState(null);

    // Edit-mode: existing saved attachment filename
    const [attachment, setAttachment] = useState(accountData?.attachment || null);
    // Add-mode: pending local File object (not yet uploaded)
    const [pendingFile, setPendingFile] = useState(null);
    // Add-mode: local blob URL preview of the pending file
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
    // Tracks whether the currently shown image failed to load
    const [imgError, setImgError] = useState(false);
    const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);
    const [attachmentBusy, setAttachmentBusy] = useState(false);
    const [attachmentNoticeKey, setAttachmentNoticeKey] = useState('');
    const [attachmentNoticeLeaving, setAttachmentNoticeLeaving] = useState(false);

    const [lightboxOpen, setLightboxOpen] = useState(false);
    const fileInputRef = useRef(null);
    const overlayRef = useRef(null);
    const formRef = useRef(null);

    useBodyScrollLock(show);
    useSheetTouchIsolation(overlayRef, show);

    const setErrorForAccountDelete = (message) => setError(message);

    // ── refresh feather icons & clear stale errors on open/close ──
    useEffect(() => {
        feather.replace();
        setError(null);
        return undefined;
    }, [show]);

    // ── reset all local state whenever accountData changes ──
    useEffect(() => {
        setSelectedType(mode === 'add' ? 'cash' : accountData?.type || 'cash');
        setShowInHome(accountData?.show_in_home ?? true);
        setLiquidMoney(accountData?.is_liquid ?? true);
        setHoverInfo(null);
        setAttachment(accountData?.attachment || null);
        setImgError(false);
        setShowDeleteAttachmentConfirm(false);
        setAttachmentBusy(false);
        setAttachmentNoticeKey('');
        setAttachmentNoticeLeaving(false);
        setPendingFile(null);
        if (pendingPreviewUrl) {
            URL.revokeObjectURL(pendingPreviewUrl);
            setPendingPreviewUrl(null);
        }
    }, [accountData, mode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Revoke blob URL on unmount
    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
        };
    }, [pendingPreviewUrl]);

    useEffect(() => {
        if (!attachmentNoticeKey) return undefined;
        setAttachmentNoticeLeaving(false);
        const leaveTimer = setTimeout(() => setAttachmentNoticeLeaving(true), 1300);
        const clearTimer = setTimeout(() => {
            setAttachmentNoticeKey('');
            setAttachmentNoticeLeaving(false);
        }, 1750);
        return () => {
            clearTimeout(leaveTimer);
            clearTimeout(clearTimer);
        };
    }, [attachmentNoticeKey]);

    const [deletedAccountId, setDeletedAccountId] = useState(null);
    const onAccountDelete = (id) => setDeletedAccountId(id);

    // ─────────────────────────────────────────────
    // Validate & store file before form submission
    // ─────────────────────────────────────────────
    const validateAndStoreFile = (file) => {
        if (!file) return;
        const errorKey = validateAttachmentFile(file, t);
        if (errorKey) {
            setError(errorKey);
            return;
        }
        setError(null);
        // Revoke any previous blob URL
        if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
        setPendingFile(file);
        setPendingPreviewUrl(URL.createObjectURL(file));
    };

    // ─────────────────────────────────────────────
    // ADD mode: pick file → store locally
    // ─────────────────────────────────────────────
    const handlePendingFileChange = (e) => {
        validateAndStoreFile(e.target.files[0]);
        e.target.value = null; // allow re-pick
    };

    // ─────────────────────────────────────────────
    // EDIT mode: pick file → upload immediately
    // ─────────────────────────────────────────────
    const handleAttachmentFileChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = null;
        if (!file) return;
        const errorKey = validateAttachmentFile(file, t);
        if (errorKey) {
            setError(errorKey);
            return;
        }

        setError(null);
        setAttachmentBusy(true);
        const formData = new FormData();
        formData.append('attachment', file);
        try {
            const res = await serverClient.post(
                `/api/user/account/${accountData.id}/attachment`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setAttachment(res.data.attachment);
            setImgError(false);
            setAttachmentNoticeKey('attachmentUploaded');
            onAccountAction();
        } catch (err) {
            if (err.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else {
                setError(err.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            setAttachmentBusy(false);
        }
    };

    const handleDeleteAttachment = async () => {
        setAttachmentBusy(true);
        setError(null);
        try {
            await serverClient.delete(`/api/user/account/${accountData.id}/attachment`);
            setAttachment(null);
            setShowDeleteAttachmentConfirm(false);
            setAttachmentNoticeKey('attachmentDeleted');
            onAccountAction();
        } catch (err) {
            if (err.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else {
                setError(err.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            setAttachmentBusy(false);
        }
    };

    const requestAttachmentDelete = () => {
        setError(null);
        setShowDeleteAttachmentConfirm(true);
    };

    const cancelAttachmentDelete = () => setShowDeleteAttachmentConfirm(false);

    const confirmAttachmentDelete = async () => {
        if (mode === 'update') {
            await handleDeleteAttachment();
            return;
        }

        URL.revokeObjectURL(pendingPreviewUrl);
        setPendingFile(null);
        setPendingPreviewUrl(null);
        setImgError(false);
        setShowDeleteAttachmentConfirm(false);
    };

    // ─────────────────────────────────────────────
    // Form submit
    // ─────────────────────────────────────────────
    const handleAccountFormSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        const payload = {
            accountName: document.getElementById('account_name').value,
            currentBalance: parseFloat(document.getElementById('current_balance').value),
            accountType: selectedType,
            accountNumber:
                selectedType !== 'cash' ? document.getElementById('account_number')?.value : null,
            showInHome,
            liquidMoney,
            timezone: getUserTimezone(),
            remarks: document.getElementById('account_remarks')?.value?.trim() || null,
        };

        try {
            if (mode === 'add') {
                const response = await serverClient.post('/api/user/account', payload);
                const newAccountId = response.data?.account_id;

                // Auto-upload pending image right after creation
                if (pendingFile && newAccountId) {
                    try {
                        const formData = new FormData();
                        formData.append('attachment', pendingFile);
                        await serverClient.post(
                            `/api/user/account/${newAccountId}/attachment`,
                            formData,
                            {
                                headers: { 'Content-Type': 'multipart/form-data' },
                            }
                        );
                    } catch (_) {
                        // non-fatal: account created, image failed silently
                    }
                }
            } else {
                await serverClient.put(`/api/user/account/${accountData.id}`, payload);
            }

            setShowToast(true);
            setTimeout(() => setShowToast(false), 2500);
            onAccountAction();
            onClose();
        } catch (error) {
            if (error.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else {
                setError(error.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAccountDelete = () => {
        setShowToastForDelete(true);
        onClose();
        onAccountAction();
    };

    const savedAttachmentUrl = attachment
        ? `${SERVER_BASE_URL}/images/account_attachments/${attachment}`
        : null;

    if (!show) return null;

    // ─────────────────────────────────────────────
    // Attachment preview block (plain render function —
    // NOT an inner component, to avoid remounts on re-render)
    // ─────────────────────────────────────────────
    const renderAttachmentSection = () => {
        const isEditMode = mode === 'update';
        const previewUrl = isEditMode ? savedAttachmentUrl : pendingPreviewUrl;
        const hasImage = Boolean(previewUrl);
        const currentFilename = isEditMode ? attachment : pendingFile?.name;
        const isImage = isPreviewableImage(currentFilename || previewUrl);

        const handleImgError = () => setImgError(true);
        const handleReplace = () => {
            setImgError(false);
            setShowDeleteAttachmentConfirm(false);
            fileInputRef.current?.click();
        };
        const handleView = () => {
            if (isImage) {
                setLightboxOpen(true);
            } else {
                window.open(previewUrl, '_blank', 'noopener,noreferrer');
            }
        };

        return (
            <div className="form-group">
                <label className="form-label">{t('accountAttachment')}</label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    style={{ display: 'none' }}
                    disabled={attachmentBusy}
                    onChange={isEditMode ? handleAttachmentFileChange : handlePendingFileChange}
                />

                {hasImage ? (
                    <div className="account-attachment-preview">
                        {/* Preview box — image or generic file badge, broken-image fallback */}
                        <div className="attachment-thumb-wrapper">
                            {attachmentBusy && (
                                <div
                                    className="attachment-action-overlay"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <span
                                        className="spinner-border spinner-border-sm"
                                        aria-hidden="true"
                                    ></span>
                                    <span>{t('uploadingAttachment')}</span>
                                </div>
                            )}
                            {imgError ? (
                                /* ── Broken image fallback ── */
                                <div className="attachment-broken">
                                    <FiAlertCircle size={32} className="broken-icon" />
                                    <span className="broken-text">{t('imageUnavailable')}</span>
                                    <button
                                        type="button"
                                        className="broken-replace-btn"
                                        disabled={attachmentBusy}
                                        onClick={handleReplace}
                                    >
                                        <FiCamera size={13} /> {t('changeAttachment')}
                                    </button>
                                </div>
                            ) : (
                                <AttachmentFilePreview
                                    url={previewUrl}
                                    filename={currentFilename}
                                    imgError={imgError}
                                    onImgError={handleImgError}
                                    onClick={handleView}
                                    className="attachment-thumb"
                                    alt="Account attachment"
                                    clickable={isImage ? isEditMode : true}
                                />
                            )}
                        </div>

                        {/* Actions */}
                        {!imgError && (
                            <div className="attachment-actions">
                                {(isEditMode || !isImage) && (
                                    <button
                                        type="button"
                                        className="btn btn-sm attachment-btn-view"
                                        disabled={attachmentBusy}
                                        onClick={handleView}
                                    >
                                        <FiEye size={13} /> {t('viewAttachment')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-sm attachment-btn-change"
                                    disabled={attachmentBusy}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <FiCamera size={13} /> {t('changeAttachment')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm attachment-btn-delete"
                                    disabled={attachmentBusy}
                                    onClick={requestAttachmentDelete}
                                >
                                    <FiTrash2 size={13} />
                                </button>
                            </div>
                        )}
                        {showDeleteAttachmentConfirm && (
                            <div
                                className="attachment-delete-confirm"
                                role="alertdialog"
                                aria-live="polite"
                            >
                                <p className="delete-confirm-text">
                                    {t('confirmAttachmentDelete')}
                                </p>
                                <div className="delete-confirm-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={cancelAttachmentDelete}
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        disabled={attachmentBusy}
                                        onClick={confirmAttachmentDelete}
                                    >
                                        {t('deleteAttachment')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {imgError && isEditMode && (
                            <div className="attachment-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm attachment-btn-delete"
                                    disabled={attachmentBusy}
                                    onClick={requestAttachmentDelete}
                                >
                                    <FiTrash2 size={13} /> {t('deleteAttachment')}
                                </button>
                            </div>
                        )}
                        {attachmentBusy && (
                            <div
                                className="attachment-inline-status attachment-inline-status-loading"
                                role="status"
                                aria-live="polite"
                            >
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                <span>{t('uploadingAttachment')}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            className="attachment-upload-placeholder"
                            disabled={attachmentBusy}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FiCamera size={22} className="attach-icon" />
                            <span className="attach-label">{t('uploadAttachment')}</span>
                            <span className="attach-hint">
                                JPEG · PNG · WebP · PDF · TXT · max 4 MB
                            </span>
                        </button>
                        {attachmentBusy && (
                            <div
                                className="attachment-inline-status attachment-inline-status-loading"
                                role="status"
                                aria-live="polite"
                            >
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                <span>{t('uploadingAttachment')}</span>
                            </div>
                        )}
                    </>
                )}
                {attachmentNoticeKey && !attachmentBusy && (
                    <div
                        className={`attachment-inline-status attachment-inline-status-success ${attachmentNoticeLeaving ? 'is-leaving' : ''}`}
                        role="status"
                        aria-live="polite"
                    >
                        <FiCheckCircle size={14} />
                        <span>{t(attachmentNoticeKey)}</span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Loader loading={loading} />
            <DeleteAccountModal
                id={deletedAccountId}
                onReturnFunc={handleAccountDelete}
                setErrorForAccountDelete={setErrorForAccountDelete}
            />

            {/* Full-screen lightbox (edit mode only) */}
            {lightboxOpen && savedAttachmentUrl && (
                <div className="account-attachment-lightbox" onClick={() => setLightboxOpen(false)}>
                    <img src={savedAttachmentUrl} alt="Attachment" className="lightbox-img" />
                    <button
                        type="button"
                        className="lightbox-close"
                        aria-label={t('close')}
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxOpen(false);
                        }}
                    >
                        &times;
                    </button>
                </div>
            )}

            <div
                ref={overlayRef}
                className="bottom-sheet-overlay open"
                onClick={onClose}
                role="presentation"
                aria-hidden={!show}
            >
                <div
                    className="auth-form bottom-sheet add-money-modal"
                    onClick={(e) => e.stopPropagation()}
                    tabIndex={-1}
                >
                    <div className="sheet-header p-2">
                        <button
                            className="close-btn dynamic-color-text"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            &times;
                        </button>
                        <h2 className="dynamic-color-text text-end">
                            {mode === 'add' ? t('addAccount') : t('updateAccount')}
                        </h2>
                    </div>

                    <div className="scrollable-content">
                        <section className="section-b-space auth-form p-2">
                            <div className="sheet-body">
                                {error && <Alert alert={'danger'}>{error}</Alert>}
                                <form
                                    ref={formRef}
                                    onSubmit={handleAccountFormSubmit}
                                    className="p-0"
                                >
                                    {/* Account Name */}
                                    <div className="form-group">
                                        <label htmlFor="account_name" className="form-label">
                                            {t('accountName')}*
                                        </label>
                                        <input
                                            required
                                            id="account_name"
                                            defaultValue={accountData?.name}
                                            type="text"
                                            className="form-control"
                                            placeholder={t('enterAccountName')}
                                        />
                                    </div>

                                    {/* Balance */}
                                    <div className="form-group">
                                        <label className="form-label">{t('currentBalance')}*</label>
                                        <input
                                            required
                                            id="current_balance"
                                            step="0.01"
                                            type="number"
                                            className="form-control"
                                            placeholder={t('enterCurrentBalance')}
                                            inputMode="decimal"
                                            defaultValue={accountData?.balance || 0}
                                        />
                                    </div>

                                    {/* Type */}
                                    <div className="form-group">
                                        <label className="form-label">{t('accountType')}</label>
                                        <select
                                            id="account_type"
                                            className="form-select"
                                            required
                                            value={selectedType}
                                            onChange={(e) => setSelectedType(e.target.value)}
                                        >
                                            <option value="general">{t('general')}</option>
                                            <option value="cash">{t('cash')}</option>
                                            <option value="current_account">
                                                {t('currentAccount')}
                                            </option>
                                            <option value="credit_card">{t('creditCard')}</option>
                                            <option value="savings_account">
                                                {t('savingsAccount')}
                                            </option>
                                            <option value="fixed_deposit">
                                                {t('fixedDeposit')}
                                            </option>
                                            <option value="dps">{t('depositPremiumScheme')}</option>
                                            <option value="loan">{t('loan')}</option>
                                            <option value="investment">{t('investment')}</option>
                                            <option value="others">{t('others')}</option>
                                        </select>
                                    </div>

                                    {/* Account number (non-cash) */}
                                    {selectedType !== 'cash' && (
                                        <div className="form-group">
                                            <label className="form-label">
                                                {t('accountNumber')}
                                            </label>
                                            <input
                                                id="account_number"
                                                defaultValue={accountData?.account_number}
                                                type="text"
                                                className="form-control"
                                                placeholder={t('enterAccountNumber')}
                                            />
                                        </div>
                                    )}

                                    {/* Attachment – both modes */}
                                    {renderAttachmentSection()}

                                    {/* Toggle Options */}
                                    <div className="account-toggle-options mt-3">
                                        {/* Show in Home */}
                                        <div className="toggle-option-card">
                                            <div className="toggle-option-left">
                                                <div className="d-flex align-items-center gap-2">
                                                    <label
                                                        htmlFor="show_in_home"
                                                        className="form-label mb-0"
                                                    >
                                                        {t('showInHome')}
                                                    </label>
                                                    <span
                                                        className="info-icon-wrapper"
                                                        onMouseEnter={() => setHoverInfo('home')}
                                                        onMouseLeave={() => setHoverInfo(null)}
                                                        onTouchStart={() =>
                                                            setHoverInfo(
                                                                hoverInfo === 'home' ? null : 'home'
                                                            )
                                                        }
                                                    >
                                                        <FiInfo
                                                            size={15}
                                                            className="info-icon-toggle"
                                                        />
                                                        {hoverInfo === 'home' && (
                                                            <span className="info-tooltip">
                                                                {t('showInHomeInfo')}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    type="checkbox"
                                                    id="show_in_home"
                                                    className="form-check-input"
                                                    role="switch"
                                                    checked={showInHome}
                                                    onChange={(e) =>
                                                        setShowInHome(e.target.checked)
                                                    }
                                                />
                                            </div>
                                        </div>

                                        {/* Liquid money */}
                                        <div className="toggle-option-card">
                                            <div className="toggle-option-left">
                                                <div className="d-flex align-items-center gap-2">
                                                    <label
                                                        htmlFor="liquid_money"
                                                        className="form-label mb-0"
                                                    >
                                                        {t('countTowardsTotal')}
                                                    </label>
                                                    <span
                                                        className="info-icon-wrapper"
                                                        onMouseEnter={() => setHoverInfo('liquid')}
                                                        onMouseLeave={() => setHoverInfo(null)}
                                                        onTouchStart={() =>
                                                            setHoverInfo(
                                                                hoverInfo === 'liquid'
                                                                    ? null
                                                                    : 'liquid'
                                                            )
                                                        }
                                                    >
                                                        <FiInfo
                                                            size={15}
                                                            className="info-icon-toggle"
                                                        />
                                                        {hoverInfo === 'liquid' && (
                                                            <span className="info-tooltip">
                                                                {t('countTowardsTotalInfo')}
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="form-check form-switch mb-0">
                                                <input
                                                    type="checkbox"
                                                    id="liquid_money"
                                                    className="form-check-input"
                                                    role="switch"
                                                    checked={liquidMoney}
                                                    onChange={(e) =>
                                                        setLiquidMoney(e.target.checked)
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Remarks – very last field */}
                                    <div className="form-group mt-3">
                                        <label htmlFor="account_remarks" className="form-label">
                                            {t('accountRemarks')}
                                        </label>
                                        <textarea
                                            id="account_remarks"
                                            className="form-control"
                                            placeholder={t('enterAccountRemarks')}
                                            defaultValue={accountData?.remarks || ''}
                                            rows={2}
                                            maxLength={1000}
                                            style={{ resize: 'none' }}
                                        />
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>

                    <div className="fixed-footer">
                        <button
                            type="submit"
                            onClick={() => formRef.current?.requestSubmit()}
                            className="btn btn-primary w-100"
                        >
                            <i className="icon button-icon" data-feather="edit"></i>
                            {mode === 'add' ? t('addAccount') : t('updateAccount')}
                        </button>
                        {mode === 'update' && (
                            <button
                                type="button"
                                className="btn btn-danger successfully w-100"
                                onClick={() => onAccountDelete(accountData?.id)}
                                data-bs-toggle="modal"
                                data-bs-target="#deleteAccount"
                            >
                                <i className="icon button-icon" data-feather="trash-2"></i>
                                {t('deleteAccount')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddEditAccountBottomSheet;
